import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { getFamilyTreeIdFromStorage } from "../../../Utils/headOfLineRole";
import {
  getDashboardMemberNameParts,
  isDashboardDeceasedDescendantMember,
} from "../../../Utils/dashboardMemberDisplayName";
import {
  getMultipleBirthLegendTags,
  getMultipleBirthSuperscriptText,
} from "../../../Utils/dashboardMultipleBirthSuperscript";
import {
  DashboardDeceasedAngel,
  DashboardPhotoHeadLine,
  DashboardPhotoModal,
  DashboardPhotoName,
  DashboardMemberNameLabel,
  DashboardNameMarkerGroup,
  getDashboardMemberNamePhotoUrls,
  getDashboardMemberPhotoUrl,
  useDashboardPhotoModal,
} from "./DashboardPhotoViewer";
import DashboardBackButton from "./DashboardBackButton";
import DashboardDetailSearchBox, {
  DashboardDetailSearchNoMatch,
} from "./DashboardDetailSearchBox";
import DashboardDetailSortFilterBar from "./DashboardDetailSortFilterBar";
import DashboardPhotoTapHint from "./DashboardPhotoTapHint";
import DashboardMarkerLegend from "./DashboardMarkerLegend";
import DashboardColorLegend from "./DashboardColorLegend";
import DashboardPeriodDropdown from "./DashboardPeriodDropdown";
import {
  buildDashboardColorLegendLines,
  buildHeadsOfLineIdByColorMap,
  extractAllMembersFromStatisticsResponse,
  extractHeadsOfLineFromStatisticsResponse,
} from "../../../Utils/dashboardFamilyLineNavigation";
import { getApi } from "../../../Services/apiService";
import { fetchFamilyHeadsGrandCount } from "../../../Utils/dashboardFamilyHeadsGrandCount";
import {
  dashboardSearchMatches,
  filterDashboardMembersBySearch,
} from "../../../Utils/dashboardDetailSearch";

/**
 * Family Head(s) - Grand Count — the printed report's layout in the Milestone
 * Wedding Anniv format: one colour-line heading per head of line with their
 * grand total, a relationship heading under it (Grandson, Gr Granddaughter …)
 * with its own count, and the members under that.
 *
 * Split into two columns the way Family Members is: the first fills the card's
 * height, the rest runs on past a divider in a second, scrolling column. Names
 * are printed with the Family Members name format, off the roster record.
 */

const recordId = (record) => {
  const id = record?.id ?? record?.member_id;
  return id == null || id === "" ? "" : String(id);
};

const getMemberSearchName = (member) =>
  [member?.first_name, member?.suffix, member?.special_name, member?.display_name]
    .filter(Boolean)
    .join(" ");

const byFirstName = (a, b) =>
  String(a?.first_name || "").toLowerCase().localeCompare(String(b?.first_name || "").toLowerCase());

/** Birth date as a timestamp, or null when the record has none. */
const getBirthTime = (member) => {
  const time = Date.parse(member?.dob || member?.date_of_birth || "");
  return Number.isFinite(time) ? time : null;
};

/** Oldest first (or youngest first); anyone without a birth date goes last. */
const byBirthDate = (descending) => (a, b) => {
  const timeA = getBirthTime(a);
  const timeB = getBirthTime(b);
  if (timeA == null && timeB == null) return 0;
  if (timeA == null) return 1;
  if (timeB == null) return -1;
  return descending ? timeB - timeA : timeA - timeB;
};

// The report already lists each group oldest first, so that is the page's
// default order and its button stays grayed out while it is in effect.
const DEFAULT_SORT = "oldest";
const ALL_LINES = "all";

const getHeadKey = (head) => String(head.headId || head.headName);

/** What a head's whole line can be found by: the head's names and the line colour. */
const getHeadSearchText = (head) =>
  [
    head.headName,
    head.raw?.head_first_name,
    head.raw?.head_middle_name,
    head.raw?.head_last_name,
    head.raw?.head_suffix,
    head.raw?.head_special_name,
    head.colorName,
    head.colorName ? `${head.colorName} Line` : "",
  ]
    .filter(Boolean)
    .join(" ");

/**
 * Relationship headings read as plurals whatever the count — "Grandson(s)",
 * "Gr Granddaughter(s)". The report sends the singular.
 */
const formatCategoryLabel = (label) => {
  const text = String(label || "").trim();
  if (!text || /\(s\)$/i.test(text)) return text;
  return `${text}(s)`;
};

/** 1 male, 2 female — the report's own codes; text values are read too. */
const getGenderCode = (member) => {
  const raw = String(member?.gender ?? "").trim().toLowerCase();
  if (raw === "1" || raw === "m" || raw === "male") return 1;
  if (raw === "2" || raw === "f" || raw === "female") return 2;
  return null;
};

/** Generation 4 is Grandson / Granddaughter, each generation after adds a "Gr ". */
const getCategoryLabel = (generationLevel, gender) =>
  `${"Gr ".repeat(generationLevel - 4)}Grand${gender === 2 ? "daughter" : "son"}`;

const normalizeColorKey = (value) => String(value || "").trim().toLowerCase();

/**
 * The report endpoint leaves deceased descendants out and has no switch to
 * keep them, so they are added back off the roster: a deceased descendant of
 * Generation 4 or below goes under the head whose line colour they carry, in
 * the relationship heading for their generation and gender. Their counts are
 * added to the head's total and the grand total.
 */
const addDeceasedFromRoster = (heads, rosterById) => {
  const listedIds = new Set();
  heads.forEach((head) =>
    head.categories.forEach((category) =>
      category.members.forEach((member) => listedIds.add(recordId(member)))
    )
  );

  const headByColor = new Map();
  heads.forEach((head) => {
    [head.hexCode, head.colorName].map(normalizeColorKey).filter(Boolean).forEach((key) => {
      if (!headByColor.has(key)) headByColor.set(key, head);
    });
  });

  const deceasedByHead = new Map();
  rosterById.forEach((member, id) => {
    if (listedIds.has(id) || !isDashboardDeceasedDescendantMember(member)) return;
    const generationLevel = parseInt(member?.generation_level ?? member?.generation, 10);
    const gender = getGenderCode(member);
    if (!Number.isFinite(generationLevel) || generationLevel < 4 || gender == null) return;

    const head = [
      member?.family_line_hex_code,
      member?.hex_code,
      member?.color_name,
      member?.family_line_color,
    ]
      .map(normalizeColorKey)
      .map((key) => headByColor.get(key))
      .find(Boolean);
    if (!head) return;

    if (!deceasedByHead.has(head)) deceasedByHead.set(head, []);
    deceasedByHead.get(head).push({ member, generationLevel, gender });
  });

  let added = 0;
  const merged = heads.map((head) => {
    const deceased = deceasedByHead.get(head);
    if (!deceased) return head;

    const categories = head.categories.map((category) => ({
      ...category,
      members: [...category.members],
    }));
    deceased.forEach(({ member, generationLevel, gender }) => {
      let category = categories.find(
        (c) => c.generationLevel === generationLevel && c.gender === gender
      );
      if (!category) {
        category = {
          label: getCategoryLabel(generationLevel, gender),
          generationLevel,
          gender,
          members: [],
        };
        categories.push(category);
      }
      category.members.push({
        ...member,
        color_name: member?.color_name || head.colorName,
        hex_code: member?.hex_code || head.hexCode,
      });
    });

    // Report order: generation, then sons before daughters; members oldest first.
    categories.sort(
      (a, b) => (a.generationLevel ?? 99) - (b.generationLevel ?? 99) || (a.gender ?? 9) - (b.gender ?? 9)
    );
    categories.forEach((category) => category.members.sort(byBirthDate(false)));

    added += deceased.length;
    return { ...head, categories, totalCount: head.totalCount + deceased.length };
  });

  return { heads: merged.filter((head) => head.categories.length > 0), added };
};

const isHeadingItem = (item) => item?.type === "head" || item?.type === "category";

/** Offset to the next row, so row gaps and heading padding count too. */
const getMeasuredItemHeight = (wrappers, index) => {
  const wrapper = wrappers[index];
  if (index + 1 < wrappers.length) {
    const delta = wrappers[index + 1].offsetTop - wrapper.offsetTop;
    if (delta > 0) return delta;
  }
  return wrapper.getBoundingClientRect().height;
};

function FamilyHeadsGrandCount() {
  const { photos: photoModalPhotos, openPhoto, closePhoto } = useDashboardPhotoModal();
  const [headsMap, setHeadsMap] = useState(() => buildHeadsOfLineIdByColorMap([]));
  const [rosterById, setRosterById] = useState(() => new Map());
  const [heads, setHeads] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [reportLoading, setLoading] = useState(true);
  const [rosterLoading, setRosterLoading] = useState(true);
  const [sortBy, setSortBy] = useState(DEFAULT_SORT);
  const [lineFilter, setLineFilter] = useState(ALL_LINES);
  const [searchQuery, setSearchQuery] = useState("");
  const [isCompactLayout, setIsCompactLayout] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 768
  );
  // The phone's footer key comes from the roster, a separate and slower call. A
  // report opened ahead of it showed a short legend that grew moments later, so
  // the phone holds the page for it; a failed roster call still lets it open.
  const loading = reportLoading || (isCompactLayout && rosterLoading);
  const [columnSplitIndex, setColumnSplitIndex] = useState(0);
  const columnsRef = useRef(null);
  const firstColumnRef = useRef(null);
  const measureListRef = useRef(null);
  const pageRef = useRef(null);
  const sortBarRef = useRef(null);
  const [searchWidth, setSearchWidth] = useState(null);

  useEffect(() => {
    const updateLayoutMode = () => setIsCompactLayout(window.innerWidth <= 768);
    updateLayoutMode();
    window.addEventListener("resize", updateLayoutMode);
    return () => window.removeEventListener("resize", updateLayoutMode);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchReport = async () => {
      try {
        setLoading(true);
        const family_tree_id = getFamilyTreeIdFromStorage();
        if (!family_tree_id) {
          setHeads([]);
          setGrandTotal(0);
          return;
        }

        const report = await fetchFamilyHeadsGrandCount(family_tree_id);
        if (cancelled) return;
        setHeads(report.heads);
        setGrandTotal(report.grandTotal);
      } catch (error) {
        console.error("Error fetching family heads grand count:", error);
        if (!cancelled) {
          setHeads([]);
          setGrandTotal(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchReport();
    return () => {
      cancelled = true;
    };
  }, []);

  // The roster supplies the full member records the Family Members name format
  // reads (suffix, special name, multiple births), photos, and the head-of-line
  // map the heading links through.
  useEffect(() => {
    const fetchRoster = async () => {
      try {
        const family_tree_id = getFamilyTreeIdFromStorage();
        if (!family_tree_id) return;

        const response = await getApi(
          `/dashboard-statistics-with-details?family_tree_id=${family_tree_id}`
        );
        if (response?.statusCode === 200 && response?.data) {
          const members = extractAllMembersFromStatisticsResponse(response.data);
          setHeadsMap(
            buildHeadsOfLineIdByColorMap(
              extractHeadsOfLineFromStatisticsResponse(response.data),
              members
            )
          );
          setRosterById(
            new Map(
              members
                .map((member) => [recordId(member), member])
                .filter(([id]) => id)
            )
          );
        }
      } catch (error) {
        console.error("Error fetching family lines:", error);
      } finally {
        setRosterLoading(false);
      }
    };

    fetchRoster();
  }, []);

  // The phone footer names the family lines the way Family Members does: off
  // the whole roster and the heads block, so both keys list the same lines.
  const legendLines = useMemo(
    () => buildDashboardColorLegendLines(Array.from(rosterById.values()), headsMap),
    [rosterById, headsMap]
  );

  const { heads: reportHeads, added: deceasedAdded } = useMemo(
    () => addDeceasedFromRoster(heads, rosterById),
    [heads, rosterById]
  );

  const isSearching = searchQuery.trim().length > 0;
  const isFiltered = isSearching || lineFilter !== ALL_LINES;

  const lineOptions = useMemo(
    () => [
      { value: ALL_LINES, label: "All Lines" },
      ...reportHeads.map((head) => ({
        value: getHeadKey(head),
        label: head.colorName ? `${head.headName} • ${head.colorName} Line` : head.headName,
        color: head.hexCode || undefined,
      })),
    ],
    [reportHeads]
  );

  const visibleHeads = useMemo(() => {
    const result = reportHeads
      .filter((head) => lineFilter === ALL_LINES || getHeadKey(head) === lineFilter)
      .map((head) => {
        // Typing the head's name or the line colour brings up the whole line.
        const headMatches = isSearching && dashboardSearchMatches(getHeadSearchText(head), searchQuery);
        const categories = head.categories
          .map((category) => {
            let members = category.members.map((member) => {
              const roster = rosterById.get(recordId(member));
              return roster ? { ...member, ...roster } : member;
            });
            if (!headMatches) {
              members = filterDashboardMembersBySearch(members, searchQuery, getMemberSearchName);
            }
            // Line Color reads each group's members A–Z, as A–Z does.
            if (sortBy === "alpha-asc" || sortBy === "color") members = [...members].sort(byFirstName);
            if (sortBy === "alpha-desc") members = [...members].sort((a, b) => byFirstName(b, a));
            if (sortBy === "oldest") members = [...members].sort(byBirthDate(false));
            if (sortBy === "youngest") members = [...members].sort(byBirthDate(true));
            return { ...category, members };
          })
          .filter((category) => category.members.length > 0);

        const count = categories.reduce((sum, c) => sum + c.members.length, 0);
        return {
          ...head,
          categories,
          // A search recounts what is on screen; otherwise the report's own total.
          displayCount: isSearching ? count : head.totalCount,
        };
      })
      .filter((head) => head.categories.length > 0);

    if (sortBy === "color") {
      return result.sort((a, b) =>
        a.colorName.toLowerCase().localeCompare(b.colorName.toLowerCase())
      );
    }
    return result;
  }, [reportHeads, rosterById, searchQuery, sortBy, lineFilter, isSearching]);

  // One flat run of rows — head heading, relationship heading, member — so the
  // list can break between any two of them when it splits into columns.
  const items = useMemo(() => {
    const list = [];
    visibleHeads.forEach((head) => {
      const headKey = head.headId || head.headName;
      list.push({ type: "head", key: `head-${headKey}`, head });
      head.categories.forEach((category) => {
        const categoryKey = `${headKey}-${category.label}-${category.generationLevel}-${category.gender}`;
        list.push({ type: "category", key: `cat-${categoryKey}`, category });
        category.members.forEach((member, index) => {
          list.push({
            type: "member",
            key: `member-${categoryKey}-${recordId(member) || index}`,
            member,
          });
        });
      });
    });
    return list;
  }, [visibleHeads]);

  const totalCount = isFiltered
    ? visibleHeads.reduce((sum, head) => sum + head.displayCount, 0)
    : grandTotal + deceasedAdded;

  // The search box ends where the last sort button does, so the two read as one
  // block. Capped by the line it sits on, so a phone keeps it on screen.
  useLayoutEffect(() => {
    const bar = sortBarRef.current?.querySelector(".dashboard-detail-sort-toggle");
    const input = pageRef.current?.querySelector(".dashboard-detail-search-input");
    if (!bar || !input) return undefined;

    const measure = () => {
      const lastButton = bar.lastElementChild;
      if (!lastButton) return;
      const width = Math.round(
        lastButton.getBoundingClientRect().right - input.getBoundingClientRect().left
      );
      setSearchWidth(width > 0 ? width : null);
    };

    measure();
    // The bar itself is always full width; its buttons are what change size —
    // the line picker grows with the line it names.
    const ro = new ResizeObserver(measure);
    ro.observe(bar);
    Array.from(bar.children).forEach((child) => ro.observe(child));
    window.addEventListener("resize", measure);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [loading, reportHeads.length, lineFilter]);

  // Split into two columns: the first fills the card's height, the second scrolls.
  useLayoutEffect(() => {
    if (loading || items.length === 0) {
      setColumnSplitIndex(0);
      return;
    }

    // Never split on a phone — one scrolling column instead.
    if (isCompactLayout) {
      setColumnSplitIndex(items.length);
      return;
    }

    const columnsEl = columnsRef.current;
    const measureEl = measureListRef.current;
    if (!columnsEl || !measureEl) return;

    const calculateSplit = () => {
      const availableHeight = columnsEl.clientHeight;
      const wrappers = measureEl.querySelectorAll(":scope > div");
      if (availableHeight <= 0 || wrappers.length === 0) {
        setColumnSplitIndex(items.length);
        return;
      }

      let cumulative = 0;
      let splitAt = wrappers.length;
      for (let i = 0; i < wrappers.length; i++) {
        const height = getMeasuredItemHeight(wrappers, i);
        if (cumulative + height > availableHeight) {
          splitAt = Math.max(1, i);
          break;
        }
        cumulative += height;
      }

      // A heading never ends the first column — it moves over with its rows.
      while (splitAt > 1 && splitAt < items.length && isHeadingItem(items[splitAt - 1])) {
        splitAt -= 1;
      }

      setColumnSplitIndex(splitAt);
    };

    calculateSplit();

    const ro = new ResizeObserver(calculateSplit);
    ro.observe(columnsEl);
    window.addEventListener("resize", calculateSplit);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", calculateSplit);
    };
  }, [items, loading, isCompactLayout]);

  const firstColumnItems = columnSplitIndex > 0 ? items.slice(0, columnSplitIndex) : items;
  const secondColumnItems =
    columnSplitIndex > 0 && columnSplitIndex < items.length ? items.slice(columnSplitIndex) : [];
  const isSingleColumnList = secondColumnItems.length === 0;

  // After render, trim the first column if its last row still runs past the bottom.
  // Desktop only: a phone's single column is meant to run past the bottom and
  // scroll, so trimming there stepped it down a row per render until React gave
  // up (update depth exceeded) and the page went blank on a long list.
  useLayoutEffect(() => {
    if (isCompactLayout) return;
    if (loading || columnSplitIndex <= 1 || columnSplitIndex >= items.length) return;

    const columnsEl = columnsRef.current;
    const lastRow = firstColumnRef.current?.lastElementChild;
    if (!columnsEl || !lastRow) return;

    if (lastRow.getBoundingClientRect().bottom > columnsEl.getBoundingClientRect().bottom + 0.5) {
      setColumnSplitIndex((prev) => Math.max(1, prev - 1));
    }
  }, [columnSplitIndex, items.length, loading, isCompactLayout]);

  const renderHeadRow = (head) => {
    const headRecord = rosterById.get(String(head.headId ?? "")) || null;
    const headMember = {
      ...(headRecord || {}),
      head_id: head.headId,
      color_name: head.colorName,
      hex_code: head.hexCode,
    };

    return (
      <div className="dashboard-detail-row family-heads-head-row">
        <span className="family-heads-head-heading dashboard-section-heading">
          <DashboardPhotoHeadLine
            name={head.headName}
            hexCode={head.hexCode}
            colorName={head.colorName ? `${head.colorName} Line` : ""}
            imageUrl={getDashboardMemberPhotoUrl(headRecord)}
            onViewPhoto={openPhoto}
            member={headMember}
            headsMap={headsMap}
          />
          <span className="family-heads-count dashboard-section-heading-count">
            ({head.displayCount})
          </span>
        </span>
      </div>
    );
  };

  const renderCategoryRow = (category) => (
    <div className="dashboard-detail-row family-heads-category-row">
      <span className="family-heads-category-label">{formatCategoryLabel(category.label)}</span>
      <span className="family-heads-count">({category.members.length})</span>
    </div>
  );

  // Same name format as Family Members: first name, suffix / special name,
  // multiple-birth superscript.
  const renderMemberRow = (member) => {
    const { baseName, descendantSpouseName } = getDashboardMemberNameParts(member, {
      appendDescendantSpouse: false,
    });
    const multipleBirthSup = getMultipleBirthSuperscriptText(member);

    return (
      <div className="dashboard-detail-row family-heads-member-row">
        <div className="dashboard-detail-name-col">
          <DashboardNameMarkerGroup>
            <DashboardPhotoName {...getDashboardMemberNamePhotoUrls(member)} onViewPhoto={openPhoto}>
              <DashboardMemberNameLabel
                baseName={baseName}
                descendantSpouseName={descendantSpouseName}
              />
            </DashboardPhotoName>
            {isDashboardDeceasedDescendantMember(member) && <DashboardDeceasedAngel />}
            {multipleBirthSup && (
              <sup className="dashboard-multiple-birth-sup">{multipleBirthSup}</sup>
            )}
          </DashboardNameMarkerGroup>
        </div>
      </div>
    );
  };

  const renderItem = (item) => {
    if (item.type === "head") return renderHeadRow(item.head);
    if (item.type === "category") return renderCategoryRow(item.category);
    return renderMemberRow(item.member);
  };

  return (
    <div
      ref={pageRef}
      className="dashboard-container family-heads-grand-count-page dashboard-detail-page"
      style={searchWidth ? { "--family-heads-search-width": `${searchWidth}px` } : undefined}
    >
      <div className="dash-box dashboard-detail-box">
        <div className="dashboard-detail-header">
          <div className="dashboard-detail-header-left">
            <DashboardBackButton />
            <h3 className="dashboard-detail-title">Family Head Grand Count</h3>
            <div className="dashboard-detail-count">{totalCount}</div>
            {!loading && (
              <DashboardDetailSearchBox value={searchQuery} onChange={setSearchQuery} />
            )}
            {!loading && (
              <div ref={sortBarRef} className="family-heads-sort-bar">
                <DashboardDetailSortFilterBar
                  sortBy={sortBy}
                  // Un-picking a sort falls back to the default order, not to none.
                  onSortChange={(next) => setSortBy(next === "none" ? DEFAULT_SORT : next)}
                  defaultSort={DEFAULT_SORT}
                  showMemberFilters={false}
                  leadingSortButtons={
                    <div className="dashboard-detail-period" role="group" aria-label="Family line">
                      <DashboardPeriodDropdown
                        label="Line"
                        options={lineOptions}
                        value={lineFilter}
                        onChange={(next) => setLineFilter(String(next))}
                      />
                      <span className="dashboard-detail-sort-divider" aria-hidden="true" />
                    </div>
                  }
                />
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="dashboard-detail-empty-state">
            <p>Loading family head grands...</p>
          </div>
        ) : items.length === 0 ? (
          <div className="dashboard-detail-empty-state">
            {isSearching ? (
              <DashboardDetailSearchNoMatch query={searchQuery} noun="family members" />
            ) : (
              <p>No data available.</p>
            )}
          </div>
        ) : (
          <div className="family-heads-content">
            <div ref={measureListRef} className="family-heads-list family-heads-list-measure" aria-hidden="true">
              {items.map((item) => (
                <div key={`measure-${item.key}`} className={`family-heads-item family-heads-item--${item.type}`}>
                  {renderItem(item)}
                </div>
              ))}
            </div>
            <div ref={columnsRef} className="family-heads-columns">
              <div
                ref={firstColumnRef}
                className={`family-heads-list family-heads-list-first${
                  isSingleColumnList ? " family-heads-list--scrollable" : ""
                }`}
              >
                {firstColumnItems.map((item) => (
                  <div key={item.key} className={`family-heads-item family-heads-item--${item.type}`}>
                    {renderItem(item)}
                  </div>
                ))}
              </div>
              {secondColumnItems.length > 0 && (
                <>
                  <div className="family-heads-column-divider" />
                  <div className="family-heads-list family-heads-list-scroll">
                    {secondColumnItems.map((item) => (
                      <div key={item.key} className={`family-heads-item family-heads-item--${item.type}`}>
                        {renderItem(item)}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        {!loading && (
          // The report lists grandchildren only — no in-laws, so no asterisk
          // to key.
          <div className="family-heads-footer in-law-footer">
            <DashboardMarkerLegend
              showInLaws={false}
              multipleBirthTags={getMultipleBirthLegendTags(
                items.filter((item) => item.type === "member").map((item) => item.member)
              )}
            />
            {/* Phone only, as on Family Members: the wide layout has the room
                to name every line in its heading and needs no key. */}
            {isCompactLayout && legendLines.length > 0 && (
              <DashboardColorLegend lines={legendLines} />
            )}
          </div>
        )}
      </div>
      <DashboardPhotoModal photos={photoModalPhotos} onClose={closePhoto} />
      <DashboardPhotoTapHint />
    </div>
  );
}

export default FamilyHeadsGrandCount;
